import React, { useEffect, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// Register font families
const Font = Quill.import("formats/font");
Font.whitelist = ["inter", "roboto", "arial", "times-new-roman"];
// Font.whitelist = ["inter", "roboto", "arial", "times-new-roman", "sans-serif", "serif", "monospace"];
Quill.register(Font, true);

// Register font sizes
const Size = Quill.import("formats/size");
Size.whitelist = ["small", "normal", "large", "huge"];
Quill.register(Size, true);

// Add CSS for font families, toolbar items, and fix overflow
const fontStyles = `
  /* Styles for editor content */
  .ql-editor .ql-font-arial {
    font-family: Arial, Helvetica, sans-serif;
  }
  .ql-editor .ql-font-times-new-roman {
    font-family: "Times New Roman", Times, serif;
  }
  .ql-editor .ql-font-sans-serif {
    font-family: sans-serif;
  }
  .ql-editor .ql-font-serif {
    font-family: serif;
  }
  .ql-editor .ql-font-monospace {
    font-family: monospace;
  }
  .ql-editor .ql-font-inter {
    font-family: "Inter", sans-serif;
  }
  .ql-editor .ql-font-roboto {
    font-family: "Roboto", sans-serif;
  }
  /* Styles for toolbar dropdown items */
  .ql-toolbar .ql-font .ql-picker-item[data-value="arial"]::before {
    content: "Arial";
    font-family: Arial, Helvetica, sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="times-new-roman"]::before {
    content: "Times New Roman";
    font-family: "Times New Roman", Times, serif;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="sans-serif"]::before {
    content: "Sans Serif";
    font-family: sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="serif"]::before {
    content: "Serif";
    font-family: serif;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="monospace"]::before {
    content: "Monospace";
    font-family: monospace;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="inter"]::before {
    content: "Inter";
    font-family: "Inter", sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-item[data-value="roboto"]::before {
    content: "Roboto";
    font-family: "Roboto", sans-serif;
  }
  /* Styles for toolbar selected font label */
  .ql-toolbar .ql-font .ql-picker-label[data-value="arial"]::before {
    content: "Arial";
    font-family: Arial, Helvetica, sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="times-new-roman"]::before {
    content: "Times New Roman";
    font-family: "Times New Roman", Times, serif;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="sans-serif"]::before {
    content: "Sans Serif";
    font-family: sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="serif"]::before {
    content: "Serif";
    font-family: serif;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="monospace"]::before {
    content: "Monospace";
    font-family: monospace;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="inter"]::before {
    content: "Inter";
    font-family: "Inter", sans-serif;
  }
  .ql-toolbar .ql-font .ql-picker-label[data-value="roboto"]::before {
    content: "Roboto";
    font-family: "Roboto", sans-serif;
  }
  /* Fix overflow in font picker dropdown */
  .ql-toolbar .ql-font .ql-picker-options {
    white-space: nowrap;
  }
  .ql-toolbar .ql-font .ql-picker-item {
    white-space: nowrap; /* Prevent text wrapping in items */
    overflow: hidden; /* Hide overflow text */
    text-overflow: ellipsis; /* Show ellipsis for very long text */
    padding: 5px 10px; /* Add padding for better spacing */
  }
  .ql-toolbar .ql-font .ql-picker-label {
    overflow: auto; /* Hide overflow text */
    text-overflow: ellipsis; /* Show ellipsis for long text */
  }
`;

// Inject font styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = fontStyles;
document.head.appendChild(styleSheet);

const CustomQuill = ({ value, readOnly = false, modules, theme, height, containerStyles = {}, ...props }) => {
  // Default modules for the toolbar
  const defaultModules = {
    toolbar: readOnly
      ? false
      : [
          [{ size: Size.whitelist }],
          [{ font: Font.whitelist }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          // ["link", "image"],
          // ["clean"],
        ],
  };

  // Generate a unique class name once per instance (stable across re-renders) to
  // avoid style conflicts. Regenerating this on every render churned the className
  // and forced the style-injection effect below to tear down and recreate a
  // <style> tag on every keystroke, triggering a global style recalculation that
  // looked like the whole page flickering while typing.
  const uniqueClassRef = useRef(`quill-container-${Math.random().toString(36).substr(2, 9)}`);
  const uniqueClass = uniqueClassRef.current;

  // Stringify so the effect only re-runs when the actual style values change,
  // not when the caller passes a brand-new object literal with the same values.
  const maxContainerHeight = props?.maxContainerHeight;
  const containerStylesKey = useMemo(
    () =>
      JSON.stringify({
        maxHeight: maxContainerHeight || "50svh",
        overflow: "auto",
        ...containerStyles,
      }),
    [containerStyles, maxContainerHeight]
  );

  // Inject dynamic styles for .ql-container.ql-snow based on default and custom styles
  useEffect(() => {
    const combinedStyles = JSON.parse(containerStylesKey);

    const dynamicStyles = `
      .${uniqueClass} .ql-container.ql-snow {
        ${Object.entries(combinedStyles)
          .map(([key, value]) => `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value};`)
          .join(" ")}
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = dynamicStyles;
    document.head.appendChild(styleSheet);

    // Cleanup on unmount / before re-injecting
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, [containerStylesKey, uniqueClass]);

  return (
    <div
      className={`custom-quill-container ${readOnly ? "quill-readonly" : ""} ${uniqueClass}`}
      style={{ height: height || "auto" }}
    >
      <ReactQuill
        key={readOnly ? "readonly" : "editable"}
        value={value}
        readOnly={readOnly}
        modules={modules || defaultModules}
        className={readOnly ? "" : "editor"}
        theme={theme || "snow"}
        style={{
          backgroundColor: readOnly ? "transparent" : "white",
          fontFamily: '"Inter", sans-serif',
        }}
        {...props}
      />
    </div>
  );
};

export default React.memo(CustomQuill);